// Error code mappings from backend docs/api-error-codes.md

export interface ErrorMessage {
  en: string;
  hu: string;
}

export const ERROR_MESSAGES: Record<string, ErrorMessage> = {
  // Media Module Errors
  MIME_NOT_ALLOWED: {
    en: 'File type not allowed. Supported: JPEG, PNG, MP4',
    hu: 'Nem támogatott fájltípus. Támogatott: JPEG, PNG, MP4',
  },
  INVALID_SIZE: {
    en: 'Invalid file size. Must be greater than 0 bytes',
    hu: 'Érvénytelen fájlméret. Nagyobb kell lennie mint 0 bájt',
  },
  FILE_TOO_LARGE: {
    en: 'File too large. Maximum: {maxSize} MB',
    hu: 'Túl nagy fájl. Maximum: {maxSize} MB',
  },
  MIME_MAGIC_REJECTED: {
    en: "File content doesn't match declared type",
    hu: 'A fájl tartalma nem egyezik a megadott típussal',
  },
  OBJECT_NOT_FOUND: {
    en: 'Media object not found or access denied',
    hu: 'Média objektum nem található vagy hozzáférés megtagadva',
  },
  OBJECT_NOT_CLEAN: {
    en: 'File is still being scanned or was flagged',
    hu: 'A fájl még vizsgálat alatt áll vagy megjelölték',
  },
  UPLOAD_INIT_FAILED: {
    en: 'Failed to initialize upload. Try again',
    hu: 'Feltöltés indítása sikertelen. Próbálja újra',
  },
  INVALID_UPLOAD_ID: {
    en: 'Invalid upload ID. Start a new upload',
    hu: 'Érvénytelen feltöltés ID. Kezdjen új feltöltést',
  },
  INVALID_PART_ETAG: {
    en: 'Invalid part checksum. Re-upload the part',
    hu: 'Érvénytelen részfájl ellenőrző összeg. Töltse fel újra',
  },
  MULTIPART_NOT_COMPLETED: {
    en: 'Upload not finalized. Call complete or abort',
    hu: 'Feltöltés nincs lezárva. Hívja meg a befejezést vagy megszakítást',
  },
  AV_SCAN_FAILED: {
    en: 'Virus scan failed. Contact support',
    hu: 'Vírusellenőrzés sikertelen. Vegye fel a kapcsolatot az ügyfélszolgálattal',
  },
  INFECTED_FILE: {
    en: 'File was flagged by security scan',
    hu: 'A fájl biztonsági vizsgálat során megjelölésre került',
  },

  // Auth Module Errors
  EMAIL_ALREADY_EXISTS: {
    en: 'Email already registered',
    hu: 'Ez az email már regisztrálva van',
  },
  INVALID_CREDENTIALS: {
    en: 'Invalid email or password',
    hu: 'Érvénytelen email vagy jelszó',
  },
  EMAIL_NOT_VERIFIED: {
    en: 'Please verify your email first',
    hu: 'Kérjük először erősítse meg az emailét',
  },
  ACCOUNT_DISABLED: {
    en: 'Account has been disabled',
    hu: 'A fiók le lett tiltva',
  },
  TOKEN_EXPIRED: {
    en: 'Session expired. Please log in again',
    hu: 'Munkamenet lejárt. Kérjük jelentkezzen be újra',
  },
  TOKEN_INVALID: {
    en: 'Invalid authentication token',
    hu: 'Érvénytelen hitelesítési token',
  },
  REFRESH_TOKEN_REUSE: {
    en: 'Security alert: Token reuse detected',
    hu: 'Biztonsági riasztás: Token újrafelhasználás észlelve',
  },

  // Owner Module Errors
  QUOTA_EXCEEDED: {
    en: 'Storage quota exceeded. Upgrade or delete files',
    hu: 'Tárhely kvóta túllépve. Frissítsen vagy töröljön fájlokat',
  },
  QUOTA_WARNING: {
    en: 'Warning: {percent}% of storage used',
    hu: 'Figyelem: {percent}% tárhely használva',
  },
  OWNER_DEVICE_NOT_FOUND: {
    en: 'Device not found',
    hu: 'Eszköz nem található',
  },
  OWNER_DEVICE_NOT_OWNER: {
    en: "You don't own this device",
    hu: 'Ez nem az Ön eszköze',
  },
  OWNER_DEVICE_ACCESS_DENIED: {
    en: 'Access denied to this device',
    hu: 'Hozzáférés megtagadva ehhez az eszközhöz',
  },

  // Collaborator Module Errors
  COLLABORATOR_ALREADY_EXISTS: {
    en: 'User is already a collaborator',
    hu: 'A felhasználó már együttműködő',
  },
  COLLABORATOR_INVITE_TOO_MANY_PENDING: {
    en: 'Too many pending invitations (max 10)',
    hu: 'Túl sok függőben lévő meghívó (max 10)',
  },

  // Transfer Module Errors
  TRANSFER_TOKEN_EXPIRED: {
    en: 'Transfer link has expired',
    hu: 'Az átadási link lejárt',
  },
  TRANSFER_EMAIL_MISMATCH: {
    en: "Email doesn't match transfer recipient",
    hu: 'Az email nem egyezik az átadás címzettjével',
  },

  // Rate Limiting
  RATE_LIMIT_EXCEEDED: {
    en: 'Too many requests. Try again in {seconds}s',
    hu: 'Túl sok kérés. Próbálja újra {seconds} másodperc múlva',
  },
  GLOBAL_RATE_LIMIT: {
    en: 'Global rate limit exceeded',
    hu: 'Globális sebességkorlát túllépve',
  },

  // Generic System Errors
  INTERNAL_SERVER_ERROR: {
    en: 'Something went wrong. Please try again',
    hu: 'Valami hiba történt. Kérjük próbálja újra',
  },
  SERVICE_UNAVAILABLE: {
    en: 'Service temporarily unavailable',
    hu: 'A szolgáltatás átmenetileg nem elérhető',
  },
  BAD_REQUEST: {
    en: 'Invalid request',
    hu: 'Érvénytelen kérés',
  },
  NOT_FOUND: {
    en: 'Resource not found',
    hu: 'Az erőforrás nem található',
  },
  UNAUTHORIZED: {
    en: 'Authentication required',
    hu: 'Hitelesítés szükséges',
  },
  FORBIDDEN: {
    en: 'Access denied',
    hu: 'Hozzáférés megtagadva',
  },
  NETWORK_ERROR: {
    en: 'Network error. Check your connection',
    hu: 'Hálózati hiba. Ellenőrizze a kapcsolatot',
  },
};

export function getErrorMessage(
  errorCode: string,
  language: 'en' | 'hu' = 'en',
  replacements?: Record<string, string | number>
): string {
  const message = ERROR_MESSAGES[errorCode];
  if (!message) {
    return errorCode; // Fallback to raw error code
  }

  let text = message[language];

  // Replace placeholders like {maxSize} with actual values
  if (replacements) {
    Object.entries(replacements).forEach(([key, value]) => {
      text = text.replace(`{${key}}`, String(value));
    });
  }

  return text;
}

export function isRetryableError(statusCode: number): boolean {
  // Retry on server errors and rate limiting
  return statusCode === 429 || (statusCode >= 500 && statusCode < 600);
}

export function shouldShowNotification(statusCode: number): boolean {
  // Don't show notifications for auth errors (handled by auth flow)
  if (statusCode === 401 || statusCode === 403) {
    return false;
  }
  return true;
}
